import { inject, Injectable } from '@angular/core';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { IFiDialogOptions, IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import {
    FiDeleteConnectedOboardingElementDialogComponent,
    IFiDeleteConnectedOboardingElementDialogData,
} from './fi-delete-connected-oboarding-element-dialog.component';
import { ObjectTypes } from '@fi-app/shared/types';
import { AppTextService } from '@fi-shared/app-text';
import { MatDialogConfig } from '@angular/material/dialog';

@Injectable()
export class FiDeleteConnectedOboardingElementDialogService {
    private fiDialogsService = inject(FiDialogsService);
    private appTextService = inject(AppTextService);

    private dialogHeaderTexts: Partial<Record<ObjectTypes, string>> = {
        [ObjectTypes.QuizQuestion]: this.appTextService.get('onboarding.quiz_confirm_delete_header'),
        [ObjectTypes.TodoItem]: this.appTextService.get('onboarding.todo_confirm_delete_header'),
        [ObjectTypes.TodoSection]: this.appTextService.get('onboarding.todo_section_confirm_delete_header'),
    };

    private messageTextIdsNoResponses: Partial<Record<ObjectTypes, string>> = {
        [ObjectTypes.QuizQuestion]: 'onboarding.quiz_confirm_delete_message_no_responses',
        [ObjectTypes.TodoItem]: 'onboarding.todo_confirm_delete_message_no_responses',
        [ObjectTypes.TodoSection]: 'onboarding.todo_section_confirm_delete_message_no_responses',
    };
    private messageTextIdsWithResponses: Partial<Record<ObjectTypes, string>> = {
        [ObjectTypes.QuizQuestion]: 'onboarding.quiz_confirm_delete_message_with_responses',
        [ObjectTypes.TodoItem]: 'onboarding.todo_confirm_delete_message_with_responses',
        [ObjectTypes.TodoSection]: 'onboarding.todo_confirm_delete_message_with_responses',
    };
    private simpleConfirmDeleteMessageTexts: Partial<Record<ObjectTypes, string>> = {
        [ObjectTypes.QuizQuestion]: 'onboarding.quiz_confirm_delete_simple_message',
        [ObjectTypes.TodoItem]: 'onboarding.todo_confirm_delete_simple_message',
        [ObjectTypes.TodoSection]: 'onboarding.todo_section_confirm_delete_simple_message',
    };

    private elementSimpleWarningDeleteMessageTexts: Partial<Record<ObjectTypes, string>> = {
        [ObjectTypes.QuizQuestion]: this.appTextService.get('quiz.delete_simple_warning'),
        [ObjectTypes.TodoItem]: this.appTextService.get('todo_item.delete_simple_warning'),
        [ObjectTypes.TodoSection]: this.appTextService.get('todo_section.delete_simple_warning'),
    };

    private baseDeleteConnectedOboardingElementDialogsConfig: MatDialogConfig = {
        panelClass: ['fi-rounded-dialog-panel', 'fi-delete-connected-oboarding-element-dialog-panel'],
        width: '90%',
        maxWidth: '560px',
        autoFocus: false,
    };

    openDialog(data: IFiDeleteConnectedOboardingElementDialogData): IFiDialogResult<void | unknown> {
        const headerText = this.dialogHeaderTexts[data.ObjectType];

        if (data.isNewElement || !data.ConnectedOnboardings?.length) {
            return this.fiDialogsService.confirmSimpleDelete({
                headerText: headerText,
                headerIconClass: 'far fa-circle-exclamation red',
                messageText: this.getElementSimpleWarningDeleteMessageText(data.ObjectType),
                yesButtonText: this.appTextService.get('common.delete'),
                noButtonText: this.appTextService.get('common.cancel'),
                isBigSizeTopBorder: true,
                preventUppercaseButtonLabel: true,
                isTopCloseButtonShown: false,
                config: {
                    ...this.baseDeleteConnectedOboardingElementDialogsConfig,
                },
            });
        }

        const yesButtonText = this.appTextService.get(data.HasResponses ? 'common.confirm' : 'common.delete');
        return this.fiDialogsService.custom({
            contentComponentRef: FiDeleteConnectedOboardingElementDialogComponent,
            contentComponentData: data,
            headerText,
            colorClass: 'red',
            noButtonText: this.appTextService.get('common.cancel'),
            headerIconClass: 'far fa-circle-exclamation red',
            isBigSizeTopBorder: true,
            preventUppercaseButtonLabel: true,
            yesButtonText,
            config: {
                ...this.baseDeleteConnectedOboardingElementDialogsConfig,
            },
        });
    }

    getMessageText(args: {
        objectType: ObjectTypes;
        connectedOnboardingsCount: number;
        hasResponses: boolean;
    }): string {
        const messageTextId = (args.hasResponses ? this.messageTextIdsWithResponses : this.messageTextIdsNoResponses)[
            args.objectType
        ];
        if (!messageTextId) {
            console.error(`Message text id not found for object type: ${args.objectType}`);
            return '';
        }
        return this.appTextService.get(messageTextId, { '{0}': args.connectedOnboardingsCount });
    }

    getSimpleConfirmDeleteMessageText(objectType: ObjectTypes): string {
        const messageTextId = this.simpleConfirmDeleteMessageTexts[objectType];
        if (!messageTextId) {
            console.error(`Simple confirm delete message text id not found for object type: ${objectType}`);
            return '';
        }
        return this.appTextService.get(messageTextId);
    }

    getElementSimpleWarningDeleteMessageText(objectType: ObjectTypes): string {
        const messageTextId = this.elementSimpleWarningDeleteMessageTexts[objectType];
        if (!messageTextId) {
            console.error(`Element simple warning delete message text id not found for object type: ${objectType}`);
            return '';
        }
        return this.appTextService.get(messageTextId);
    }
}
